import type { ComputedRef, Ref } from 'vue';
import type { DetailPageContext } from '#layers/block-engine/public';
import type { FormBlockSuccessEventPayload } from '#layers/blocks/types/form-block';
import type { AnyItem, CompositePrimaryKey, GlobalCollectionId, PrimitivePrimaryKey, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { FormSaveError, FormSaveHandler, FormWriteContext, RStoreFormObject } from '#layers/forms/public';
import { tryOnScopeDispose } from '@vueuse/core';
import { ref } from 'vue';
import { useRouter } from '#app/composables/router';
import { getItemPrimaryKey, isSameKey } from '#layers/data-model/public';
import { useFormSubmitHandler } from '#layers/forms/public';
import { useCollectionRoutes } from '#layers/spaces/public';

export interface UseFormBlockSubmitParams {
	formObject: Ref<RStoreFormObject<AnyItem> | null>;
	writeContext: Readonly<Ref<FormWriteContext>>;
	isCreate: ComputedRef<boolean>;
	itemId: ComputedRef<CompositePrimaryKey | null>;
	finalCollectionId: ComputedRef<GlobalCollectionId | undefined>;
	collectionData: ComputedRef<ResolvedMonospaceCollection | undefined>;
	isDetailPageForm: ComputedRef<boolean>;
	detailPageContext: Pick<DetailPageContext, 'formMode'>;
	captureSaveHandlers: () => readonly FormSaveHandler[];
	saveHandlerErrors: Ref<FormSaveError[]>;
	clearSaveHandlerErrors: () => void;
	emitOnRootBlock: (event: string, payload: FormBlockSuccessEventPayload) => void;
	onSuccess?: (payload: FormBlockSuccessEventPayload) => void;
	onSubmit?: (data: { data: AnyItem }) => void;
	hasExternalChanges: () => boolean;
	runOperation?: <T>(operation: () => Promise<T>) => Promise<T>;
}

export interface UseFormBlockSubmitReturn {
	onSubmit: () => Promise<void>;
	/** Form-only submit state; external form handlers are accounted for by useFormBlock. */
	formOnlySubmitDisabled: ComputedRef<boolean>;
	submitError: Ref<Error | null>;
	submitted: Ref<boolean>;
}

/** Handles persisted, emitted, and unbound form submissions. */
export function useFormBlockSubmit(params: UseFormBlockSubmitParams): UseFormBlockSubmitReturn {
	const {
		formObject,
		writeContext,
		isCreate,
		itemId,
		finalCollectionId,
		collectionData,
		isDetailPageForm,
		detailPageContext,
		captureSaveHandlers,
		saveHandlerErrors,
		clearSaveHandlerErrors,
		emitOnRootBlock,
		onSuccess,
		onSubmit: onSubmitCallback,
		hasExternalChanges,
		runOperation = (operation) => operation(),
	} = params;

	const submitted = ref(false);
	let submittedSaveHandlers: readonly FormSaveHandler[] = [];
	let submittedCollection: ResolvedMonospaceCollection | undefined;
	let submissionInFlight = false;
	let active = true;

	tryOnScopeDispose(() => {
		active = false;
	});

	const router = useRouter();
	const { detailRoute } = useCollectionRoutes(collectionData);
	const { onSubmit: handleSubmit, submitDisabled: formOnlySubmitDisabled, submitError } = useFormSubmitHandler<AnyItem>(formObject, {
		isCreate,
		onBeforeSubmit: () => {
			submitted.value = false;
			clearSaveHandlerErrors();
		},
		onSuccess: async (result) => {
			await handleFormSuccess(result, {
				collection: submittedCollection,
				saveHandlers: submittedSaveHandlers,
				triggerSaveHandlers: true,
			});
		},
	});

	/** Captures an independent snapshot through the form object's value-aware clone API. */
	function cloneFormData(): AnyItem {
		return (formObject.value?.$getRawData({ clone: true }) as AnyItem | undefined) ?? {};
	}

	/** Saves external changes when the form's own fields need no write. */
	async function triggerSaveHandlersOnly(saveHandlers: readonly FormSaveHandler[]) {
		if (!formObject.value || !submittedCollection) return;
		submitted.value = false;
		clearSaveHandlerErrors();

		await handleFormSuccess(cloneFormData(), {
			collection: submittedCollection,
			saveHandlers,
			triggerSaveHandlers: true,
		});
	}

	/** Captures one submission and holds its guard until the owning operation finishes. */
	async function onSubmit() {
		if (submissionInFlight || (finalCollectionId.value && !writeContext.value.available)) return;
		submissionInFlight = true;
		submittedCollection = collectionData.value;

		return runOperation(async () => {
			try {
				onSubmitCallback?.({ data: cloneFormData() });

				if (!detailPageContext.formMode || detailPageContext.formMode === 'submit') {
					if (!formOnlySubmitDisabled.value) {
						submittedSaveHandlers = captureSaveHandlers();
						await handleSubmit();
					}
					else if (hasExternalChanges()) {
						await triggerSaveHandlersOnly(captureSaveHandlers());
					}
				}
				else {
					submitted.value = false;
					clearSaveHandlerErrors();

					try {
						const result = cloneFormData();
						const data = submittedCollection
							? writeContext.value.projectPayload(result, submittedCollection)
							: result;

						await handleFormSuccess(result, {
							collection: submittedCollection,
							data,
							triggerSaveHandlers: false,
						});

						submitError.value = null;
					}
					catch (error) {
						submitError.value = error instanceof Error ? error : new Error(String(error));
					}
				}
			}
			finally {
				submittedSaveHandlers = [];
				submittedCollection = undefined;
				submissionInFlight = false;
			}
		});
	}

	return { onSubmit, formOnlySubmitDisabled, submitError, submitted };

	/** Publishes submission success after any requested save handlers finish. */
	async function handleFormSuccess(
		result: AnyItem,
		options: {
			collection: ResolvedMonospaceCollection | undefined;
			data?: AnyItem;
			saveHandlers?: readonly FormSaveHandler[];
			triggerSaveHandlers: boolean;
		},
	) {
		const successPayload = createSuccessPayload(result, options.collection, options.data ?? result);

		if (options.triggerSaveHandlers && hasUsableItemKey(successPayload.key)) {
			const handlerResults = await runSaveHandlers(options.saveHandlers ?? [], successPayload.key);
			if (!active) return;
			saveHandlerErrors.value = handlerResults.flat().filter((error): error is FormSaveError => !!error);
		}

		if (!active) return;
		submitted.value = true;
		onSuccess?.(successPayload);

		if (isDetailPageForm.value) {
			emitOnRootBlock('success', successPayload);
		}

		redirectToDetail(successPayload.key, options.collection);
	}

	/** Pairs submitted data with its collection primary key or optional local id. */
	function createSuccessPayload(result: AnyItem, collection: ResolvedMonospaceCollection | undefined, data: AnyItem): FormBlockSuccessEventPayload {
		if (collection) return { key: getItemPrimaryKey(result, collection), data };
		if (finalCollectionId.value) throw new Error('Collection data unavailable during form success handling');
		return { key: getLocalFormItemKey(result), data };
	}

	/** Navigates persisted detail forms after creation or a primary-key change. */
	function redirectToDetail(itemKey: CompositePrimaryKey, collection: ResolvedMonospaceCollection | undefined) {
		if (!isDetailPageForm.value || !collection || (detailPageContext.formMode && detailPageContext.formMode !== 'submit')) return;

		if (isCreate.value || (itemId.value !== null && !isSameKey(itemId.value, itemKey))) {
			const route = detailRoute(itemKey);
			if (route) router.push(route);
		}
	}
}

/** Extracts an unbound form's id key when its data supplies a string or number. */
function getLocalFormItemKey(result: AnyItem): CompositePrimaryKey {
	return isPrimitivePrimaryKey(result.id) ? { id: result.id } : {};
}

/** Narrows values supported by primitive item keys. */
function isPrimitivePrimaryKey(value: unknown): value is PrimitivePrimaryKey {
	return typeof value === 'string' || typeof value === 'number';
}

/** Checks whether saved data supplies a key for external save handlers. */
function hasUsableItemKey(itemKey: CompositePrimaryKey): boolean {
	return Object.keys(itemKey).length > 0;
}

/** Runs captured external save handlers against the submitted item key. */
function runSaveHandlers(handlers: readonly FormSaveHandler[], itemKey: CompositePrimaryKey): Promise<FormSaveError[][]> {
	return Promise.all(handlers.map((handler) => handler({ itemKey })));
}
